/**
 * The Sell wizard's words — the five steps a director walks through to
 * record a sale of a consigned work and turn its code red.
 *
 * Its own file rather than more keys on `GALLERY_COPY`, because every line
 * here is read moments before somebody moves title to another person's
 * picture and binds a covenant for years. Which step can be left and how the
 * typing becomes minor units is `sellWizard.model.ts`; the draft, the buyer
 * check and the one write are `useSellWizardController.ts`.
 *
 * The fee figures are read from the shared tariff and never restated, so a
 * preview cannot disagree with the row it previews. Where the design and the
 * registry disagree — Convert offers title rather than transferring it, the
 * fee is invoiced rather than taken, there is no agreement document on file —
 * the copy says so on the screen rather than in a comment nobody can read.
 */

import { REGISTRATION_FEE_CURRENCY, REGISTRATION_FEE_MINOR, formatMoney } from "@ondo/shared";

/** '$150' — the tariff as the wizard prints it. */
function formatFee(minor: number): string {
  return formatMoney({ minor, currency: REGISTRATION_FEE_CURRENCY }, "en-US");
}

/**
 * The Sell wizard's own copy — the five steps, and the three things they must
 * not say.
 *
 * A third block rather than more keys on `GALLERY_COPY`, for the reason
 * `SOLD_COPY` gives one screen earlier: this one is about MONEY, and about a
 * covenant that binds for years. Every sentence has to survive being read by a
 * director immediately before they move somebody else's title.
 *
 * THREE PLACES THE DESIGN AND THE REGISTRY DISAGREE, each answered in the copy
 * rather than in a comment nobody on the screen can read:
 *
 * 1. The design's step 5 says "On convert, ownership transfers." It does not.
 *    `ondo.effect_sale()` records the sale and OFFERS title —
 *    `status = 'offered'` for a buyer on ONDO, `'unregistered'` for one who is
 *    not. The consignment ends and the code goes red immediately, both true;
 *    title lands when the buyer accepts. `convertEffect` says exactly that, and
 *    the transfers migration says the same sentence about itself.
 *
 * 2. The design's step 4 charges $150 / $350. No payments provider exists, so
 *    the step NAMES the fee and takes nothing; the row in
 *    `ondo.registration_fees` is written by a trigger when the sale lands
 *    (20260910110000) and the Transaction Centre records it paid. `feeNote`
 *    says it is invoiced separately, because a screen that implied money had
 *    moved would be the feed-as-truth bug in a costlier register.
 *
 * 3. The design's step 1 shows a "Consignment agreement — ✓ ON FILE" document
 *    card. There is no document anywhere in the schema. The step shows the real
 *    terms off the consignment row instead; see `consignmentFacts`.
 */
export const SELL_COPY = {
  title: "Sell: green ONDO code → red conversion",
  close: "Close",

  /** The stepper. Order lives in `SELL_STEPS`; these are only the words. */
  steps: {
    consignment: "Consignment",
    buyer: "Buyer",
    covenant: "Post-sale covenant",
    fee: "Fee",
    convert: "Convert",
  },

  back: "← Back",
  next: "Continue →",
  submit: "Convert & complete sale",
  submitting: "Recording the sale…",

  /** Used when a consignment names no currency, which is a real arrangement. */
  defaultCurrency: "GBP",

  // ── 1 · Consignment ──────────────────────────────────────────────────────
  /**
   * The lead, and it states the authority rather than describing the screen.
   *
   * `POST /v1/consignments/:id/sale` is addressed to the consignment because the
   * consignment IS the authority — a work with no live accepted one has no door
   * at all — and a director who knows that reads the rest of the wizard
   * correctly.
   */
  consignmentLead:
    "A sale can only proceed on a work the artist or estate has consigned to this gallery. These are the terms the sale will be recorded under.",
  /**
   * Why there is no document card here, said on the screen and not only in code.
   *
   * The alternative was a card reading "✓ ON FILE", which would be the screen
   * asserting a record that does not exist — and this is the last screen in the
   * product where an invented assurance would be harmless.
   */
  noDocument:
    "ONDO holds the terms of this consignment, not the signed agreement itself. What is shown here is the record.",
  consignmentContinue:
    "The green code was issued to the artist or estate and consigned to this gallery. Continue to record the sale.",

  facts: {
    consignor: "Consignor",
    consignorId: "Consignor ONDO id",
    arrangement: "Arrangement",
    consignedOn: "Consigned",
    basis: "Basis",
    asking: "Asking price",
    floor: "Floor",
    note: "Note",
  },

  arrangement: {
    consignment: "Consignment",
    primary_representation: "Primary — represented artist",
  },

  /** Matched to `ondo.consignment_basis`, as `GALLERY_COPY.basis` is. */
  basis: {
    net_of_sale: "Net-of-sale",
    min_price_agreed: "Minimum price agreed",
    unspecified: "",
  },

  // ── 2 · Buyer ────────────────────────────────────────────────────────────
  buyerLegend: "Buyer / new owner",
  /**
   * The toggle, and NEITHER SIDE OF IT SEARCHES.
   *
   * "Match on network" submits an ONDO id the gallery was handed; "Create new
   * owner" submits the seller's own typing against no identity at all. No
   * endpoint resolves a name to an ONDO id and none may — an ONDO id is
   * attached, never discovered. `ondo.effect_sale()` resolves the id inside a
   * definer and answers with two verdicts, charging the probe budget on the
   * refusal, so the id space stays unwalkable from here too.
   */
  matchOnNetwork: "Match on network",
  createNewOwner: "Create new owner",
  matchHelp:
    "An ONDO id the buyer gave you. Nothing here looks one up — an ONDO id is attached, never discovered.",
  createHelp:
    "A buyer who is not on ONDO. The sale is recorded against the name you write; title stays where it is until they join and claim it.",
  /**
   * Confirming the buyer, which is a GATE here and a courtesy on the agent
   * dialog.
   *
   * The difference is what a wrong id costs. A mandate is ended with one call
   * and nothing moved; a sale offers a stranger title to somebody else's
   * picture, and the only correction is a withdrawal that stays on the record.
   * So the name is read back and agreed to before Continue lights.
   *
   * `principals_read` is `acts_as(id)`, so the gallery cannot see this person's
   * row at all — `ondo.transferee_check()` is the only thing in the registry
   * that can say the name before title moves, and it says it only for an id that
   * may actually receive this work.
   */
  checking: "Checking this ONDO id…",
  foundLegend: "This ONDO id belongs to",
  confirmBuyer: "Yes — this is the buyer I mean to transfer title to.",
  confirmHelp:
    "Read the name before you agree. A sale can only be unsaid by withdrawing it, which stays on the work’s record.",

  buyerOndoLabel: "Owner ONDO id",
  buyerOndoPlaceholder: "ONDO-…",
  buyerNameLabel: "Buyer name",
  buyerNamePlaceholder: "e.g. Michael Hue-Williams",

  priceLabel: "Sale price",
  currencyLabel: "Currency",
  priceHelp:
    "The agreed figure. Recorded to the penny and shown rounded — a third decimal place is refused rather than rounded away.",
  addVat: "Add VAT",
  addVatHelp: "gallery / auction sales; the seller sets the scheme",
  vatSchemeLabel: "VAT scheme",
  vatAmountLabel: "VAT amount",
  vatHelp:
    "Leave the amount empty to record the scheme without a figure. Empty is not zero — “not recorded here” and “none charged” are different facts.",

  channelLabel: "Channel",
  channel: {
    gallery: "Gallery",
    auction: "Auction",
    private: "Private",
  },
  vatScheme: {
    none: "None",
    margin: "Margin",
    standard: "Standard",
    zero: "Zero-rated",
  },
  auctionNote:
    "Selling at auction? Auction houses operate through the gallery or agent account — enter the hammer price as the agreed price above.",

  // ── 3 · Post-sale covenant ───────────────────────────────────────────────
  covenantToggle: "Attach a post-sale covenant",
  /**
   * Fixed at this sale, and that is the schema rather than a policy.
   *
   * `ondo.transfer_covenants` carries an append-only trigger; a term cannot be
   * edited afterwards by anybody, including the gallery that wrote it. Only
   * `unlocked_at` ever changes, and that releases the lock without touching a
   * term. Saying so before the sliders is the difference between a control a
   * director sets carefully and one they expect to revisit.
   */
  covenantHelp:
    "Terms are fixed at this sale — a percentage of resale profit only, with no later variation. Attaching one creates an automatic right of sale and a premium registration fee.",
  termLabel: (years: number) =>
    `Restriction term — ${years} ${years === 1 ? "year" : "years"} from sale`,
  shareLabel: (pct: number) => `Revenue share — ${pct}% of resale profit`,
  shareHelp:
    "A percentage of PROFIT on a resale within the term — resale price less this one — not of the resale price. Locked at signature.",
  splitLabel: (gallery: number, artist: number) =>
    `Split — gallery ${gallery}% / artist ${artist}%`,
  artistElected:
    "Artist requires a share of resale revenue — the artist or estate has elected to receive their split of any resale profit under this covenant.",
  locksTransfer:
    "Lock the red code — it cannot be transferred on until the term expires, or this gallery unlocks it.",
  /**
   * The one refusal this form cannot predict, named where it can still be
   * avoided.
   *
   * A lock is enforced by `ondo.effect_sale()` rather than displayed by it, and
   * `ConsignedWork` carries no lock state — so the NEXT gallery to sell this
   * work meets a 403 with no warning on their row. Saying it here is the only
   * place in the product where the person creating that consequence is present.
   */
  locksNote:
    "A locked work cannot be sold again by anyone, including this gallery, until the term ends or the lock is lifted from Resale settlements.",

  covenantDefaults: {
    termYears: 5,
    resaleSharePct: 50,
    gallerySharePct: 50,
  },
  termMax: 25,

  // ── 4 · Fee ──────────────────────────────────────────────────────────────
  feeLegend: (premium: boolean) =>
    premium
      ? "Premium registration fee (covenant attached)"
      : "Base registration fee",
  /**
   * The two figures, READ FROM THE SHARED TARIFF and not restated. This block
   * used to be the third copy of "$150 / $350"; `REGISTRATION_FEE_MINOR` is
   * the second and `ondo.registration_tariff()` the first, and rls.sh asserts
   * those two agree. A preview that disagreed with the row it previews would
   * be the worse of two wrong states.
   */
  fee: {
    plain: formatFee(REGISTRATION_FEE_MINOR.plain),
    premium: formatFee(REGISTRATION_FEE_MINOR.premium),
  },
  feePlainChip: `Base ${formatFee(REGISTRATION_FEE_MINOR.plain)} · plain`,
  feePremiumChip: `Premium ${formatFee(REGISTRATION_FEE_MINOR.premium)} · covenant`,
  feeCurrency: REGISTRATION_FEE_CURRENCY,
  /**
   * WHAT THIS STEP DOES, said on the step — and it changed with
   * 20260910110000. There is a fee row now, written by trigger the moment the
   * sale is recorded, and the Transaction Centre lists it. What there still is
   * not is a provider: ONDO invoices the fee and records the firm's word that
   * it was paid. Nothing is taken here.
   */
  feeNote:
    "Applied once, when the code flips green → red. The fee is recorded against this sale and listed in the Transaction Centre; ONDO invoices it separately and takes nothing here.",

  // ── 5 · Convert ──────────────────────────────────────────────────────────
  /**
   * What Convert ACTUALLY does, against the design's "ownership transfers".
   *
   * It cannot transfer ownership in one press when the buyer is a principal who
   * has consented to nothing — the same argument the access model makes in the
   * other direction, that "a registry that treated one as a transfer would let
   * anybody reassign anybody's picture by typing". So the sale is recorded, the
   * consignment ends, the code goes red, and title is OFFERED.
   */
  convertEffect: {
    ondo: "On convert the sale is recorded, this consignment ends, the code flips green → red, and the work moves to Sold Works. Title is OFFERED to the buyer and moves when they accept it — until then the ledger reads “awaiting buyer”.",
    name: "On convert the sale is recorded, this consignment ends, the code flips green → red, and the work moves to Sold Works. The buyer is not on ONDO, so there is nobody to accept: title stays where it is, and the ledger reads “buyer not on ONDO” until they join and claim it.",
  },
  /**
   * The one gate on the last step, and it is a STATEMENT rather than a validation.
   *
   * The design says "I confirm I have full unencumbered title", which a gallery
   * selling on consignment does not have and must not claim: the consignor holds
   * title and the gallery holds the authority to sell. Saying it the design's way
   * would have a director attest to something false on every ordinary sale.
   */
  confirmTitle:
    "I confirm this gallery has the authority to sell this work under the consignment above, and that title is unencumbered.",

  summary: {
    work: "Work",
    buyer: "Buyer",
    price: "Sale price",
    channel: "Channel",
    vat: "VAT",
    code: "Code",
    codeRed: "green → red",
    codeLocked: "green → red · locked",
    covenant: "Post-sale covenant",
    noCovenant: "None",
    covenantTerms: (years: number, share: number, gallery: number, artist: number) =>
      `${years}yr · ${share}% profit · gallery ${gallery}% / artist ${artist}%`,
    fee: "Registration fee",
  },

  errors: {
    /**
     * Only for the statuses that carry no sentence of their own. Every refusal
     * `ondo.effect_sale()` makes arrives with the definer's own words, and
     * `describeSaleError` prefers them — see its docblock for why.
     */
    refused: "The registry would not record this sale.",
    throttled: "Too many buyer ids have been refused recently. Wait a few minutes and try again.",
    signedOut: "Your session has expired. Sign in again to record this sale.",
  },
} as const;
